import { i18nMark } from '@lingui/react'
import { parse } from 'query-string'

import {
  PRESCRIPTION_TYPE_FREE,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_PRIVAT,
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
  PRICE_TYPES,
} from 'shared/consts/productTemplates'
import {
  DELIVERY_10_14_DAYS,
  DELIVERY_20_30_DAYS,
  DELIVERY_3_5_DAYS,
  DELIVERY_3_DAYS,
  DELIVERY_5_7_DAYS,
  DELIVERY_5_DAYS,
  DELIVERY_7_10_DAYS,
} from 'shared/consts/stockStatus'
import { PROMOTION_TYPES } from 'views/components/organisms/ProductLabels/consts'
import {
  getColorByStatusReason,
  getLabelByStatusReason,
  getStatusByStatusReason,
} from 'views/utils/productStatusUtils'
import { getListPriceSuperScript } from 'views/utils/productUtils'

import getImageUrl from './getImageUrl'

const STOCK_STATUS_COLORS = {
  [DELIVERY_3_DAYS]: 'green',
  [DELIVERY_3_5_DAYS]: 'green',
  [DELIVERY_5_DAYS]: 'green',
  [DELIVERY_5_7_DAYS]: 'orange',
  [DELIVERY_7_10_DAYS]: 'orange',
  [DELIVERY_10_14_DAYS]: 'orange',
  [DELIVERY_20_30_DAYS]: 'orange',
  default: '',
}

// Skipped labels will be added in the deeper components
// (e.g. in `ProductLabelPrescription`)
const SKIP_LABELS = ['rezeptpflichtig']
const PHARMA_OBL_ATTRIBUTE = 'apothekenpflichtig'
const ALGOLIA_PROMOTIONS = {
  '1 1': PROMOTION_TYPES.ONE_FOR_ONE,
  '1+1': PROMOTION_TYPES.ONE_FOR_ONE,
  bundle: PROMOTION_TYPES.BUNDLE,
  Sparsets: PROMOTION_TYPES.BUNDLE,
  Gratisaktion: PROMOTION_TYPES.FREEBIE,
}
const MAX_PER_PAGE_LIMIT = 5
const PER_PAGE_RANGE = 10
const updateStockColor = (stock, notOnStockMessage) => {
  if (stock && stock.includes('available') && notOnStockMessage) {
    return STOCK_STATUS_COLORS[notOnStockMessage]
  }

  return getColorByStatusReason(stock)
}

const sortByUnit = (
  { unit: unitA, unitQuantityString: unitQuantityStringA },
  { unit: unitB, unitQuantityString: unitQuantityStringB }
) => {
  return unitA - unitB || unitQuantityStringA - unitQuantityStringB
}

const getRetailPrice = ({ price, currency }) => {
  const netValue = Number(price) || 0

  return {
    info: null,
    net: netValue,
    currency,
    amount: netValue / 100,
    type: PRICE_TYPES.RETAIL,
  }
}

const getListPriceInfo = (superScripts, pharmaObl) => ({
  text: i18nMark('general.label.listPrice'),
  index: getListPriceSuperScript({ superScripts, pharmaObl }),
})

const getListPrice = ({ superScripts, pharmaObl, price, currency }) => {
  const netValue = Number(price) || 0
  const { index, text } = getListPriceInfo(superScripts, pharmaObl)

  return {
    info: {
      index,
      text,
    },
    net: netValue,
    currency,
    amount: netValue / 100,
    type: PRICE_TYPES.LIST,
  }
}

const mapAttributesToLabels = (attributes = []) =>
  attributes.filter(label => !SKIP_LABELS.includes(label))

const getValidRedpointsMultiplier = ({
  RPMultiplier = 1,
  RPStartDate = 0,
  RPEndDate = 9999999999999,
}) => {
  if (RPMultiplier && RPMultiplier > 1) {
    const now = Date.now()
    if (RPStartDate < now && now < RPEndDate) {
      return RPMultiplier
    }

    return 1
  }

  return RPMultiplier
}

const mapVariants = ({
  variants,
  superScripts,
  pharmaObl,
  cdnUrl,
  isRebrandingEnabled,
  currency,
}) =>
  variants.map(
    ({
      id,
      webDetailPath,
      stock,
      packSize,
      discount,
      text,
      retailPrice,
      listPrice,
      attributes,
      pricePerUnit,
      imageUrl,
      ean,
      pzn,
      notOnStockMessage,
      ...commonProps
    }) => {
      let unitQuantityString = null
      let unit = null

      if (packSize) {
        const data = String(packSize).split(/\s/)
        unitQuantityString = data[0]
        unit = data[1]
      }

      const multiplierRP = getValidRedpointsMultiplier(commonProps)

      return {
        ...commonProps,
        id,
        descriptionShort: text,
        unitQuantityString,
        unit,
        ean,
        pzn,
        labels: mapAttributesToLabels(attributes),
        canonicalURL: webDetailPath || '',
        unitPricing: pricePerUnit || '',
        imageUrl: getImageUrl({
          product: { image: imageUrl },
          width: 256,
          cdnUrl,
          isRebrandingEnabled,
        }),
        stockStatus: {
          notOnStockMessage,
          statusReason: getLabelByStatusReason(stock),
          statusColor: updateStockColor(stock, notOnStockMessage),
          status: getStatusByStatusReason(stock),
        },
        discount: Number(discount),
        prices: [
          getRetailPrice({ price: retailPrice, currency }),
          getListPrice({ price: listPrice, superScripts, pharmaObl, currency }),
        ],
        ...(multiplierRP && {
          multiplierRP:
            multiplierRP && !Number.isNaN(multiplierRP)
              ? parseInt(multiplierRP, 10)
              : undefined,
        }),
        objectID: id,
      }
    }
  )

const mapPromotions = product => {
  const {
    applicablePromotions = '',
    promotionProduct = {},
    ribbonText,
  } = product
  return {
    ...product,
    promotion: ALGOLIA_PROMOTIONS[parse(applicablePromotions).promotion],
    promotionProduct: {
      ...promotionProduct,
      ribbonText,
    },
  }
}

const mapPrices = ({
  amount,
  bonus,
  freePrescriptionPrice,
  insurancePrescriptionPrice,
  listPrice,
  prescriptionMedicine,
  prescriptionType,
  price,
  currency,
  privatePrescriptionPrice,
  superScripts,
  pharmaObl,
}) => {
  const pricesList = [
    getListPrice({ price: listPrice, superScripts, pharmaObl, currency }),
    getRetailPrice({ price, currency }),
  ]

  if (prescriptionMedicine) {
    const selector = {
      [PRESCRIPTION_TYPE_NO_PRESCRIPTION]: price,
      [PRESCRIPTION_TYPE_INSURANCE]: insurancePrescriptionPrice,
      [PRESCRIPTION_TYPE_FREE]: freePrescriptionPrice,
      [PRESCRIPTION_TYPE_PRIVAT]: privatePrescriptionPrice,
    }

    pricesList.push({
      amount: amount || 0,
      bonus,
      currency,
      net: selector[prescriptionType] / 100,
      type: PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionType],
    })
  }

  return pricesList
}

/**
 * NB: Algolia doesn't return product props:
 *    + free, famId, bonus, prescriptionType, amount
 */
export const convertAlgoliaHitToProductProps = ({
  product,
  superScripts,
  isRebrandingEnabled = false,
  currency,
}) => {
  const {
    activeSubstances = [],
    additiveSubstances = [],
    amount,
    attributes = [],
    averageRating,
    bonus,
    canonicalURL,
    deeplink = '',
    discountInPercent,
    freePrescriptionPrice,
    hasImage,
    image,
    insurancePrescriptionPrice,
    listPrice,
    nowProductGroup,
    notOnStockMessage,
    pharmaForm,
    prescriptionMedicine,
    prescriptionType,
    price,
    prices = {},
    pricePerUnit,
    privatePrescriptionPrice,
    productName = '',
    pzn,
    id,
    ratingCount,
    stockStatus,
    stockStatusReason,
    variants = [],
    flags = {},
    sponsored,
    cdnUrl,
    ...commonProps
  } = product

  const pharmaObl = attributes.includes(PHARMA_OBL_ATTRIBUTE)
  return {
    ...commonProps,
    ...mapPromotions(commonProps),
    activeSubstances: activeSubstances.join(', '),
    additiveSubstances: additiveSubstances.join(', '),
    canonicalURL: canonicalURL || deeplink,
    darreichung: pharmaForm,
    deeplink,
    discountPercent: discountInPercent,
    discount: discountInPercent,
    famId: null,
    free: null,
    pzn,
    flags: { ...flags, prescriptionMedicine },
    id: id ?? pzn,
    imageUrlLarge: getImageUrl({ product, cdnUrl, isRebrandingEnabled }),
    nowProductGroup,
    labels: mapAttributesToLabels(attributes),
    name: productName,
    numberOfRatings: ratingCount,
    pharmaForm,
    rating: averageRating,
    ratingCount,
    unitPricing: pricePerUnit || '',
    variants: mapVariants({
      variants,
      superScripts,
      pharmaObl,
      cdnUrl,
      isRebrandingEnabled,
      currency,
    }).sort(sortByUnit),
    imageUrl: getImageUrl({ product, width: 256, cdnUrl, isRebrandingEnabled }),
    prescriptionType,
    prices: mapPrices({
      amount,
      bonus,
      freePrescriptionPrice,
      insurancePrescriptionPrice,
      listPrice,
      prescriptionMedicine,
      prescriptionType,
      price,
      prices,
      currency,
      privatePrescriptionPrice,
      superScripts,
      pharmaObl,
      ...commonProps,
    }),
    stockStatus: {
      notOnStockMessage,
      statusReason: getLabelByStatusReason(stockStatusReason),
      statusColor: getColorByStatusReason(stockStatusReason),
      status: stockStatus,
    },
    sponsored,
    multiplierRP: getValidRedpointsMultiplier(product),
  }
}

export const interact = (refine, value) => event => {
  try {
    refine(value || event.target.value)
    // eslint-disable-next-line no-empty
  } catch (_) {}
}

export const getRealMinForRatings = min => (min > 0 ? min - 0.5 : min)

export const getRealMaxForRatings = max => (max < 5 ? max + 0.49 : max)

export const getHitsPerPageItems = () => {
  const limitOptions = new Array(MAX_PER_PAGE_LIMIT)
    .fill(null)
    .map((_, i) => (i + 1) * PER_PAGE_RANGE)

  return limitOptions.map(option => ({
    value: option,
    label: `${option}`,
  }))
}

export const getAlgoliaUserDataProductsByType = (
  userData,
  labelType = 'labels'
) => {
  if (!Array.isArray(userData) || !userData.length) {
    return {}
  }
  const objects =
    userData?.find(({ type }) => type === labelType)?.objects || []

  return objects.reduce((acc, cur) => ({ ...acc, ...cur }), {})
}
